import { OAuthApi } from '@backstage/core-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';

/**
 * Backstage's `RefreshingAuthSessionManager` only refreshes a session
 * proactively when `getAccessToken()` is called within 180s (3 min) of expiry.
 *
 * Polling on a shorter interval guarantees a call always lands inside that
 * window, so the refresh happens in the background instead of blocking a
 * user-facing request.
 *
 * Calls made outside the window are cache hits and
 * perform no network I/O.
 */
const WARM_INTERVAL_MS = 2 * 60 * 1000;

export class BackendAuthProd implements BackendAuthApi {
  private readonly okta?: OAuthApi;

  constructor(options: { okta?: OAuthApi }) {
    this.okta = options.okta;

    if (this.okta) {
      this.startWarming();
    }
  }

  async getToken(): Promise<string> {
    let token = '';

    try {
      token = (await this.okta?.getAccessToken()) ?? '';
    } catch (_) {
      // ignore
    }

    return token;
  }

  /**
   * Backstage already refreshes the session on page load, so
   * an immediate call would at best dedupe and at worst add a competing
   * refresh during the load window.
   *
   * We only cover the session's later lifetime
   * via the interval warming.
   *
   * It runs for the app's lifetime
   * and are torn down with the page, so there is nothing to clean up.
   */
  private startWarming(): void {
    setInterval(() => this.getToken(), WARM_INTERVAL_MS);
  }
}
